// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  formatCompactElapsedTime,
} from "../core/format.js";
import {
  reportActionText,
} from "../findings/presentation.js";

export const reportDisclaimerParagraphs = [
  "This report is generated by an automated analysis system and is provided for initial informational context only. It should not be treated as a definitive diagnosis, maintenance recommendation, airworthiness determination, or substitute for inspection by a certified A&P mechanic or other qualified aviation maintenance professional.",
  "Automated analysis may incorrectly identify issues that are not actually present, and may fail to identify issues that do exist. The findings in this report should therefore be reviewed as possible indicators or discussion points, not confirmed conclusions.",
  "Any final diagnosis, inspection, repair, maintenance action, or return-to-service determination should be made by qualified personnel in accordance with applicable regulations, manufacturer guidance, and accepted maintenance practices.",
];

export function reportFileName(entry, firstPoint, dateLabel) {
  const aircraft = entry?.aircraft ?? "flight";
  const date = dateLabel(firstPoint, entry).replace(/,/g, "").replace(/\s+/g, "-");
  return `${aircraft}-${date}-engine-report.pdf`.replace(/[^a-z0-9._-]+/gi, "-");
}

export function reportScoreLine(metric) {
  if (!metric) return "No data";
  const score = Number.isFinite(metric.score) ? `${metric.score}/100` : "No score";
  return `${score} - ${metric.note ?? ""}`.trim();
}

export function reportTurboLoggedChannels(details = {}) {
  return [
    Number.isFinite(details.peakTit) ? "TIT" : null,
    Number.isFinite(details.peakMap) ? "MAP" : null,
    Number.isFinite(details.peakUpperDeck) ? "upper deck pressure" : null,
    Number.isFinite(details.peakCompressor) ? "compressor discharge temp" : null,
    Number.isFinite(details.peakInduction) ? "induction temp" : null,
  ].filter(Boolean);
}

export function reportTurboTitQualityCue(metric) {
  const details = metric?.details ?? {};
  const quality = details.titQuality;
  if (!quality) return false;
  return (details.titQualityPenalty ?? 0) > 0
    || quality.score < 88
    || Boolean(quality.window);
}

export function turboTitQualitySummaryText(details) {
  const quality = details?.titQuality;
  if (!quality) return "";
  const label = quality.label ?? "TIT";
  const unstable = quality.unstableSampleCount ?? 0;
  const outOfRange = quality.outOfRangeCount ?? 0;
  const zero = quality.zeroCount ?? 0;
  const jumps = quality.impossibleJumpCount ?? 0;
  const flaggedSamples = Math.max(unstable, outOfRange);

  const hasWindow = Number.isFinite(quality.window?.startTime) && Number.isFinite(quality.window?.endTime);
  const windowText = hasWindow
    ? ` between ${formatCompactElapsedTime(quality.window.startTime)} and ${formatCompactElapsedTime(quality.window.endTime)}`
    : "";

  const extras = [];
  if (jumps > 0 && Number.isFinite(quality.maxJumpDelta)) {
    extras.push(`Some readings jumped by as much as ${Math.round(quality.maxJumpDelta)} F between consecutive samples — more than the engine can physically change`);
  } else if (jumps > 0) {
    extras.push(`${jumps.toLocaleString()} reading${jumps === 1 ? "" : "s"} jumped further than the engine can physically change between samples`);
  }
  if (zero > 0) {
    extras.push(`${zero.toLocaleString()} reading${zero === 1 ? "" : "s"} dropped to zero or went missing entirely`);
  }
  const extrasText = extras.length ? ` ${extras.join(". ")}.` : "";

  if (flaggedSamples > 0) {
    return `The ${label} probe was reading erratically${windowText} — about ${flaggedSamples.toLocaleString()} ${label} reading${flaggedSamples === 1 ? "" : "s"} did not look like real engine behavior.${extrasText}`;
  }
  if (jumps > 0) {
    const deltaText = Number.isFinite(quality.maxJumpDelta) ? ` (largest about ${Math.round(quality.maxJumpDelta)} F)` : "";
    return `The ${label} signal jumped between values that the engine can't physically produce — ${jumps.toLocaleString()} time${jumps === 1 ? "" : "s"}${deltaText}${windowText}.`;
  }
  if (zero > 0) {
    return `The ${label} probe dropped to zero or went missing on ${zero.toLocaleString()} reading${zero === 1 ? "" : "s"}${windowText}.`;
  }
  return `${label} signal quality scored ${Math.round(quality.score)}/100.`;
}

export function reportTurboContextStatus(metric) {
  const details = metric?.details ?? {};
  const loggedChannels = reportTurboLoggedChannels(details);
  if (!loggedChannels.length) return "Need turbo telemetry";
  if (reportTurboTitQualityCue(metric)) return "TIT signal unreliable";
  if (details.status === "needs-profile-limits" || !details.limitCount) return "Profile limits needed";
  if (details.status === "limit-review") return "Turbo limit review";
  if (details.status === "data-quality-review") return "TIT signal review";
  if (details.status === "heat-margin-review") return "High-power heat review";
  if (details.status === "heat-margin-watch") return "High-power heat watch";
  if (details.status === "margin-watch") return "TIT margin watch";
  if (details.status === "pass") return "Inside configured limits";
  return metric?.note ?? "Turbo context";
}

export function reportTurboContextNote(metric) {
  const details = metric?.details ?? {};
  const loggedChannels = reportTurboLoggedChannels(details);
  if (!loggedChannels.length) {
    return "The aircraft profile indicates turbo context, but this log does not include MAP, TIT, upper-deck pressure, compressor, or induction-temperature channels for turbo scoring.";
  }
  if (reportTurboTitQualityCue(metric)) {
    const qualityText = turboTitQualitySummaryText(details);
    const limitText = details.limitCount
      ? `MAP/TIT limit checks are secondary here because the TIT trace is not trustworthy, even if the numeric peaks are below the configured limits.`
      : "Configured MAP/TIT limits are still needed, but the immediate issue is TIT signal quality.";
    return `${qualityText || "The TIT signal shows a data-quality problem."} ${limitText}`;
  }
  if (["heat-margin-review", "heat-margin-watch"].includes(details.status) && details.highPowerHeat?.window) {
    const heat = details.highPowerHeat.window;
    const fuelText = Number.isFinite(heat.expectedFuelFlowMin) && Number.isFinite(heat.fuelFlowP10)
      ? ` Fuel flow p10 was ${heat.fuelFlowP10.toFixed(1)} GPH against a configured ${heat.expectedFuelFlowMin.toFixed(1)} GPH minimum.`
      : " No configured high-power fuel-flow minimum was available, so this is a lower-confidence heat-margin cue.";
    const heatText = [
      Number.isFinite(heat.map) ? `MAP ${heat.map.toFixed(1)} inHg` : null,
      Number.isFinite(heat.rpm) ? `${Math.round(heat.rpm)} RPM` : null,
      Number.isFinite(heat.peakTit) ? `peak TIT ${Math.round(heat.peakTit)} F` : null,
      Number.isFinite(heat.peakCht) ? `peak CHT ${Math.round(heat.peakCht)} F` : null,
    ].filter(Boolean).join(", ");
    return `The turbo review flagged high-power heat context${heatText ? ` (${heatText})` : ""}.${fuelText}`;
  }
  const channelText = loggedChannels.join(", ");
  const basis = details.limitCount
    ? `compares them with ${details.limitCount} configured profile limit${details.limitCount === 1 ? "" : "s"}`
    : "shows them as evidence because configured MAP/TIT limits are missing";
  return `Logged turbo channels: ${channelText}. The report ${basis}. ${metric?.note ?? ""}`.trim();
}

export function reportTurboContextReview(metric) {
  const details = metric?.details ?? {};
  const loggedChannels = reportTurboLoggedChannels(details);
  if (!loggedChannels.length) {
    return "Confirm the monitor export includes turbo channels if this aircraft should be scored for turbo/boost behavior.";
  }
  if (reportTurboTitQualityCue(metric)) {
    return "Do not treat this as a clean turbo pass. Inspect the raw TIT probe/wiring/logger channel first, then compare MAP, fuel flow, RPM, CHT, OAT, altitude, and nearby flights.";
  }
  if (!details.limitCount) {
    return "Add POH/AFM max MAP, max TIT, and applicable temperature limits before treating turbo status as pass/fail.";
  }
  if (details.status === "pass") {
    return "Use this as baseline context and keep watching peak MAP, peak TIT, TIT p95, and upper-deck pressure across similar flights.";
  }
  if (["heat-margin-review", "heat-margin-watch"].includes(details.status)) {
    return "Review the marked high-power window with MAP, RPM, fuel flow, TIT, CHT, OAT, altitude, and configured fuel-flow expectations visible.";
  }
  return "Review the marked turbo window with MAP, TIT, fuel flow, RPM, CHT, OAT, altitude, and profile-limit entries visible.";
}

export function reportWindowSummary(windows) {
  if (!windows?.length) return "No marked window";
  return windows.slice(0, 5).map((window) => {
    const start = formatCompactElapsedTime(window.startTime);
    const end = Number.isFinite(window.endTime) ? formatCompactElapsedTime(window.endTime) : start;
    return window.label ? `${window.label}` : `${start}-${end}`;
  }).join("; ");
}

export function reportWriteLesson(writer, lesson) {
  if (!lesson) return;
  writer.keyValue("What it watches", lesson.watches);
  writer.keyValue("What it means", lesson.meaning);
  writer.keyValue("How to review it", lesson.review);
}

export function reportWriteActions(writer, actions = [], limit = 3) {
  actions.slice(0, limit).forEach(([priority, action]) => {
    writer.bullet(reportActionText(priority, action));
  });
}
